import React, { forwardRef, useRef, useMemo } from 'react';
import { NavLink } from '@leagueplatform/routing';
import { tabStyles, TabIconLabelWrapper } from '#components/tabs/tab.tsx';
import { useTablistContext } from '#components/tabs/tablist-context.tsx';
import { useTabElementIsInView } from '#components/tabs/use-tab-element-is-in-view.tsx';
import { Icon } from '#components/icon/icon.tsx';
import { styled } from '../../theme';
import { useResponsiveProp } from '../../hooks/use-responsive-prop';
import { useComposedRefs } from '../../hooks/use-composed-ref';
import { GDSLinkProps, NavItem } from './nav-link';

export interface GDSRouterLinkProps extends GDSLinkProps {
  id: string;
  path: string;
}

const DISPLAY_NAME = 'RouterLink';

const BaseRouterLink = styled(NavLink, {
  ...tabStyles,
});

export const RouterLink = forwardRef<HTMLAnchorElement, GDSRouterLinkProps>(
  (
    {
      children,
      className,
      css,
      icon,
      iconLabel,
      id,
      path,
      width,
      'data-testid': testId = `GDS-${DISPLAY_NAME}`,
      ...props
    },
    ref,
  ) => {
    const internalRef = useRef<HTMLAnchorElement>(null);
    const composedRefs = useComposedRefs(ref, internalRef);
    const tabWidth = useResponsiveProp(width);
    const tabIcon = useResponsiveProp(icon);
    const { elementIsInView, tabScrollOptions } = useTabElementIsInView();
    const { scrollBackButton, scrollForwardButton } = useTablistContext();

    const scrollElementIfNotInView = useMemo(
      () => () => {
        if (
          !elementIsInView(internalRef) &&
          (scrollBackButton || scrollForwardButton)
        ) {
          internalRef.current!.scrollIntoView(tabScrollOptions);
        }
      },
      [
        elementIsInView,
        tabScrollOptions,
        scrollBackButton,
        scrollForwardButton,
      ],
    );

    return (
      <NavItem
        className={[`GDS-nav-item`, className].join(' ')}
        css={{
          ...css,
          ...(width && {
            width: `${tabWidth}`,
          }),
        }}
        data-testid={testId}
      >
        <BaseRouterLink
          className="GDS-nav-router-link"
          id={id}
          to={path}
          ref={composedRefs}
          onFocus={() => scrollElementIfNotInView()}
          {...props}
        >
          <TabIconLabelWrapper>
            {tabIcon && <Icon icon={tabIcon} label={iconLabel} />}
            {children}
          </TabIconLabelWrapper>
        </BaseRouterLink>
      </NavItem>
    );
  },
);
